import React, { useEffect, useMemo, useState } from 'react';
import { FaCheckCircle, FaChevronLeft, FaChevronRight, FaRegClock } from 'react-icons/fa';

// Gestion publique d'un rendez-vous via le lien envoyé par email : /rendez-vous/gestion?token=X
// Permet de replanifier ou d'annuler tant que le RDV est à venir et non traité.
export default function GestionRdvPage() {
  const token = new URLSearchParams(window.location.search).get('token');
  const apiBase = process.env.REACT_APP_API_ENDPOINT;
  const clientTz = useMemo(() => {
    try {
      return Intl.DateTimeFormat().resolvedOptions().timeZone;
    } catch {
      return '';
    }
  }, []);

  const [info, setInfo] = useState(null); // { name, typeName, slug, durationMin, slotStartUtc, status, editable }
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [mode, setMode] = useState('view'); // view | reschedule | canceled | done
  const [slots, setSlots] = useState([]);
  const [slotsLoading, setSlotsLoading] = useState(false);
  const [selectedDay, setSelectedDay] = useState(null);
  const [selectedSlot, setSelectedSlot] = useState(null);
  const [submitting, setSubmitting] = useState(false);

  const loadInfo = async () => {
    setLoading(true);
    setError('');
    try {
      const res = await fetch(`${apiBase}/v1/public/booking/manage/${encodeURIComponent(token)}`);
      if (!res.ok) throw new Error('Lien invalide ou rendez-vous introuvable.');
      setInfo(await res.json());
    } catch (e) {
      setError(e.message || 'Erreur de chargement.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!token) {
      setError('Lien invalide.');
      setLoading(false);
      return;
    }
    loadInfo();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  const loadSlots = async () => {
    if (!info?.slug) return;
    setSlotsLoading(true);
    try {
      const res = await fetch(`${apiBase}/v1/public/booking/${encodeURIComponent(info.slug)}/slots`);
      const data = await res.json();
      setSlots(Array.isArray(data.slots) ? data.slots : []);
    } catch {
      setSlots([]);
    } finally {
      setSlotsLoading(false);
    }
  };

  const dayKeys = useMemo(() => {
    const map = {};
    for (const s of slots) {
      const d = new Date(s.startUtc);
      const key = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
      if (!map[key]) map[key] = [];
      map[key].push(s);
    }
    return map;
  }, [slots]);
  const sortedDays = useMemo(() => Object.keys(dayKeys).sort(), [dayKeys]);
  useEffect(() => {
    if (mode === 'reschedule' && !selectedDay && sortedDays.length) setSelectedDay(sortedDays[0]);
  }, [sortedDays, selectedDay, mode]);
  const daySlots = selectedDay ? dayKeys[selectedDay] || [] : [];

  const fmtFull = (ms) =>
    new Date(ms).toLocaleString('fr-FR', {
      weekday: 'long',
      day: 'numeric',
      month: 'long',
      hour: '2-digit',
      minute: '2-digit',
    });
  const fmtDayLabel = (key) => {
    const [y, m, d] = key.split('-').map(Number);
    return new Date(y, m - 1, d).toLocaleDateString('fr-FR', { weekday: 'long', day: 'numeric', month: 'long' });
  };
  const fmtTime = (ms) => new Date(ms).toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' });

  const startReschedule = async () => {
    setMode('reschedule');
    setSelectedSlot(null);
    setSelectedDay(null);
    await loadSlots();
  };

  const doCancel = async () => {
    setSubmitting(true);
    setError('');
    try {
      const res = await fetch(`${apiBase}/v1/public/booking/manage/${encodeURIComponent(token)}/cancel`, {
        method: 'POST',
      });
      if (!res.ok) {
        const d = await res.json().catch(() => ({}));
        if ((d?.message || '').startsWith('NOT_EDITABLE')) throw new Error('Ce rendez-vous ne peut plus être annulé.');
        throw new Error('Annulation impossible.');
      }
      setMode('canceled');
    } catch (e) {
      setError(e.message || 'Erreur.');
    } finally {
      setSubmitting(false);
    }
  };

  const doReschedule = async () => {
    if (!selectedSlot) {
      setError('Choisissez un nouveau créneau.');
      return;
    }
    setSubmitting(true);
    setError('');
    try {
      const res = await fetch(`${apiBase}/v1/public/booking/manage/${encodeURIComponent(token)}/reschedule`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ slotStartUtc: selectedSlot, clientTimezone: clientTz }),
      });
      const d = await res.json().catch(() => ({}));
      if (!res.ok) {
        const msg = d?.message || '';
        if (msg.startsWith('SLOT_TAKEN')) {
          setError('Ce créneau vient d\'être pris. Choisissez-en un autre.');
          setSelectedSlot(null);
          await loadSlots();
          return;
        }
        if (msg.startsWith('NOT_EDITABLE')) throw new Error('Ce rendez-vous ne peut plus être modifié.');
        throw new Error('Replanification impossible.');
      }
      await loadInfo();
      setMode('done');
    } catch (e) {
      setError(e.message || 'Erreur.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 flex flex-col items-center justify-center px-4 py-10">
      <div className="w-full max-w-2xl">
        <div className="text-center mb-6">
          <span className="text-3xl font-extrabold">
            <span style={{ color: '#113039' }}>Oui</span>
            <span style={{ color: '#f89133' }}>Goo</span>
          </span>
        </div>

        <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-6">
          {loading ? (
            <p className="text-center text-gray-500 py-10">Chargement</p>
          ) : error && !info ? (
            <p className="text-center text-red-600 py-10">{error}</p>
          ) : mode === 'canceled' ? (
            <div className="text-center py-6">
              <FaCheckCircle className="mx-auto w-12 h-12 text-green-500 mb-3" />
              <h2 className="text-lg font-bold text-gray-900">Rendez-vous annulé</h2>
              <p className="mt-2 text-sm text-gray-600">Votre rendez-vous a bien été annulé.</p>
            </div>
          ) : mode === 'done' ? (
            <div className="text-center py-6">
              <FaCheckCircle className="mx-auto w-12 h-12 text-green-500 mb-3" />
              <h2 className="text-lg font-bold text-gray-900">Rendez-vous replanifié</h2>
              <p className="mt-2 text-sm text-gray-600">
                Nouveau créneau : <span className="font-semibold">{fmtFull(info.slotStartUtc)}</span>. Un email de
                confirmation vous a été envoyé.
              </p>
            </div>
          ) : info && !info.editable && mode === 'view' ? (
            <div className="text-center py-6">
              <h2 className="text-lg font-bold text-gray-900">Rendez-vous non modifiable</h2>
              <p className="mt-2 text-sm text-gray-600">
                Ce rendez-vous est déjà passé ou traité. Contactez-nous si besoin.
              </p>
            </div>
          ) : mode === 'reschedule' ? (
            <>
              <button
                type="button"
                onClick={() => setMode('view')}
                className="mb-3 flex items-center gap-1 text-sm text-gray-500 hover:text-gray-800"
              >
                <FaChevronLeft className="w-3 h-3" /> Retour
              </button>
              <h1 className="text-xl font-bold text-gray-900">Choisir un nouveau créneau</h1>
              {slotsLoading ? (
                <p className="mt-6 text-center text-gray-500 py-8">Chargement des créneaux</p>
              ) : sortedDays.length === 0 ? (
                <p className="mt-6 text-center text-sm text-gray-500 py-8">Aucun créneau disponible pour le moment.</p>
              ) : (
                <>
                  <div className="mt-5 flex items-center gap-2">
                    <button
                      type="button"
                      onClick={() => {
                        const i = sortedDays.indexOf(selectedDay);
                        if (i > 0) {
                          setSelectedDay(sortedDays[i - 1]);
                          setSelectedSlot(null);
                        }
                      }}
                      disabled={sortedDays.indexOf(selectedDay) <= 0}
                      className="flex items-center justify-center w-9 h-9 rounded-full bg-gray-100 text-gray-600 hover:bg-gray-200 disabled:opacity-40"
                    >
                      <FaChevronLeft className="w-3.5 h-3.5" />
                    </button>
                    <div className="flex-1 text-center font-semibold capitalize text-gray-800">
                      {selectedDay ? fmtDayLabel(selectedDay) : ''}
                    </div>
                    <button
                      type="button"
                      onClick={() => {
                        const i = sortedDays.indexOf(selectedDay);
                        if (i < sortedDays.length - 1) {
                          setSelectedDay(sortedDays[i + 1]);
                          setSelectedSlot(null);
                        }
                      }}
                      disabled={sortedDays.indexOf(selectedDay) >= sortedDays.length - 1}
                      className="flex items-center justify-center w-9 h-9 rounded-full bg-gray-100 text-gray-600 hover:bg-gray-200 disabled:opacity-40"
                    >
                      <FaChevronRight className="w-3.5 h-3.5" />
                    </button>
                  </div>
                  <div className="mt-4 grid grid-cols-3 gap-2 sm:grid-cols-4">
                    {daySlots.map((s) => (
                      <button
                        key={s.startUtc}
                        type="button"
                        onClick={() => setSelectedSlot(s.startUtc)}
                        className={`rounded-xl border py-2.5 text-sm font-medium transition ${
                          selectedSlot === s.startUtc
                            ? 'border-transparent text-white'
                            : 'border-gray-200 text-gray-700 hover:border-orange-300'
                        }`}
                        style={selectedSlot === s.startUtc ? { backgroundColor: '#f89133' } : {}}
                      >
                        {fmtTime(s.startUtc)}
                      </button>
                    ))}
                  </div>
                  {error && <p className="mt-3 text-sm text-red-600">{error}</p>}
                  <button
                    type="button"
                    onClick={doReschedule}
                    disabled={submitting || !selectedSlot}
                    className="mt-5 w-full rounded-xl py-3.5 text-white font-semibold transition disabled:opacity-50"
                    style={{ backgroundColor: '#f89133' }}
                  >
                    {submitting ? 'Envoi en cours' : 'Confirmer le nouveau créneau'}
                  </button>
                </>
              )}
            </>
          ) : (
            <>
              <h1 className="text-xl font-bold text-gray-900">{info.typeName || 'Votre rendez-vous'}</h1>
              <p className="mt-1 flex items-center gap-1.5 text-sm text-gray-600">
                <FaRegClock className="w-3.5 h-3.5 text-gray-400" /> {fmtFull(info.slotStartUtc)}
              </p>
              {error && <p className="mt-3 text-sm text-red-600">{error}</p>}
              <div className="mt-6 flex flex-col gap-3 sm:flex-row">
                <button
                  type="button"
                  onClick={startReschedule}
                  className="flex-1 rounded-xl py-3 text-white font-semibold transition"
                  style={{ backgroundColor: '#f89133' }}
                >
                  Replanifier
                </button>
                <button
                  type="button"
                  onClick={doCancel}
                  disabled={submitting}
                  className="flex-1 rounded-xl border border-red-200 py-3 font-semibold text-red-600 transition hover:bg-red-50 disabled:opacity-50"
                >
                  {submitting ? 'Annulation en cours' : 'Annuler le rendez-vous'}
                </button>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
